import { FilterOutlined, LoadingOutlined } from '@ant-design/icons'
import { usePageTitle, useView } from '@hooks';
import { Button, Checkbox, Empty, Spin, Table } from 'antd'
import { ExportDropdown } from './ExportDropdown'
import { ExpandableConfig } from 'antd/lib/table/interface'
import { SizeType } from 'antd/lib/config-provider/SizeContext'
import React, { useEffect, useRef, useState } from 'react'
import './SharedTable.css';

interface IProps {
  loading?: boolean;
  children?: any;
  emptyText?: React.ReactNode | (() => React.ReactNode);
  columns?: any;
  maxResultCount?: number;
  dataSource: any[];
  totalCount: number;
  sizeType?: SizeType;
  currentPage?: number;
  handlePageChange?: (pagination: any) => void;
  summary?: ((data: readonly any[]) => React.ReactNode) | undefined;
  enableRowClick?: boolean;
  onRowClick?: (record: any) => void;
  bottomMargin?: number;
  expandable?: ExpandableConfig<any>;
  mobileFilters?: React.ReactNode;
  exportable?: boolean;
  exportFileName?: string;
  exportColumns?: any[];
  onExportAll?: () => Promise<any[]>;
  mobileSelectable?: boolean;
  selectedIds?: string[];
  onSelectChange?: (checked: boolean, record: any) => void;
  isSelectDisabled?: (record: any) => boolean;
}

export const SharedTable: React.FC<IProps> = ({
  loading,
  children,
  columns,
  maxResultCount = 15,
  dataSource,
  totalCount,
  emptyText = <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data" />,
  currentPage,
  summary,
  handlePageChange,
  sizeType = 'small',
  enableRowClick = false,
  onRowClick,
  bottomMargin = 30,
  expandable,
  mobileFilters,
  exportable = false,
  exportFileName = 'export',
  exportColumns,
  onExportAll,
  mobileSelectable = false,
  selectedIds = [],
  onSelectChange,
  isSelectDisabled,
}) => {
  const { isMobile } = useView();
  const [scrollY, setScrollY] = useState<number | undefined>(undefined);
  const tableRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
  const scrollTop = sessionStorage.getItem('tableScrollTop');
  if (scrollTop && tableRef.current) {
    const scrollContainer = tableRef.current.querySelector('.ant-table-body') as HTMLElement;
    if (scrollContainer) {
      scrollContainer.scrollTop = parseInt(scrollTop, 10);
    }
  }
}, [dataSource]);

  useEffect(() => {
    const updateHeight = () => {
      if (!containerRef.current) return;
      
      const rect = containerRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const availableHeight = viewportHeight - rect.top - bottomMargin;
      
      const tableElement = containerRef.current.querySelector('.ant-table');
      if (!tableElement) return;
      
      const tableHeader = tableElement.querySelector('.ant-table-thead') as HTMLElement;
      const tablePagination = containerRef.current.querySelector('.ant-pagination') as HTMLElement;
      const tableSummary = tableElement.querySelector('.ant-table-summary') as HTMLElement;
      
      const headerHeight = tableHeader?.offsetHeight || 0;
      const paginationHeight = tablePagination?.offsetHeight || 0;
      const summaryHeight = tableSummary?.offsetHeight || 0;
      
      const extraSpacing = 35;
      
      const calculatedScrollY = availableHeight - headerHeight - paginationHeight - summaryHeight - extraSpacing;
      
      const minHeight = 200;
      setScrollY(Math.max(calculatedScrollY, minHeight));
    };

    const timeoutId = setTimeout(updateHeight, 100);
    
    updateHeight();
    window.addEventListener('resize', updateHeight);
    
    return () => {
      window.removeEventListener('resize', updateHeight);
      clearTimeout(timeoutId);
    };
  }, [bottomMargin, dataSource.length, totalCount]);

  const handleRowClick = (record: any) => {
    const scrollContainer = tableRef.current?.querySelector('.ant-table-body') as HTMLElement;
    const scrollTop = scrollContainer?.scrollTop ?? 0;
    sessionStorage.setItem('tableScrollTop', scrollTop.toString());
    if (onRowClick) {
      onRowClick(record);
    }
  };

  const [showFilters, setShowFilters] = useState(false);

  const { setExtraHeaderActions } = usePageTitle();

  // Push filter button + export button into header
  const hasMobileFilters = !!mobileFilters;
  const exportCols = exportColumns || columns;

  // Stable key: only re-run when actual data IDs change, not on every render
  const dataKey = dataSource.map((r) => r.id).join(',');

  useEffect(() => {
    const actions: React.ReactNode[] = [];

    if (exportable) {
      actions.push(
        <ExportDropdown
          key="table-export"
          columns={exportCols || []}
          dataSource={dataSource}
          fileName={exportFileName}
          onFetchAll={onExportAll}
        />
      );
    }

    if (isMobile && hasMobileFilters) {
      actions.push(
        <Button
          key="table-filter"
          icon={<FilterOutlined />}
          onClick={() => setShowFilters((prev) => !prev)}
          style={{ background: '#52c41a', borderColor: '#52c41a', color: '#fff' }}
        />
      );
    }

    if (actions.length > 0) {
      setExtraHeaderActions(<>{actions}</>);
    }

    return () => setExtraHeaderActions(null);
  }, [isMobile, hasMobileFilters, exportable, exportFileName, dataKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Accumulate items for mobile load-more
  const [accumulatedData, setAccumulatedData] = useState<any[]>([]);
  const prevDataRef = useRef<string>('');

  useEffect(() => {
    if (!isMobile) return;
    const ids = dataSource.map((r) => r.id).join(',');
    if (ids === prevDataRef.current) return;
    prevDataRef.current = ids;

    if ((currentPage ?? 1) === 1) {
      setAccumulatedData(dataSource);
    } else {
      setAccumulatedData((prev) => {
        const existingIds = new Set(prev.map((r) => r.id));
        const newItems = dataSource.filter((r) => !existingIds.has(r.id));
        return [...prev, ...newItems];
      });
    }
  }, [isMobile, dataSource, currentPage]);

  const hasMore = accumulatedData.length < totalCount;
  const handleLoadMore = () => {
    const nextPage = (currentPage ?? 1) + 1;
    handlePageChange?.({ current: nextPage, pageSize: maxResultCount });
  };

  if (isMobile) {
    const visibleColumns = (columns || []).filter((col: any) => col.key !== 'checkbox');

    return (
      <div className="shared-table-container">
        {/* Collapsible filters */}
        {showFilters && mobileFilters && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 8,
            padding: '10px 12px',
            marginBottom: 8,
            background: '#fafafa',
            borderRadius: 8,
            border: '1px solid #f0f0f0',
          }}>
            {mobileFilters}
          </div>
        )}

        <Spin spinning={!!loading && accumulatedData.length === 0} indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}>
          {accumulatedData.length === 0 && !loading ? (
            <div style={{ padding: 24 }}>{typeof emptyText === 'function' ? emptyText() : emptyText}</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {accumulatedData.map((record: any) => (
                <div
                  key={record.id}
                  onClick={(e) => {
                    const target = e.target as HTMLElement;
                    if (target.closest('.ant-checkbox-wrapper') || target.closest('.ant-checkbox')) return;
                    if (enableRowClick && onRowClick) onRowClick(record);
                  }}
                  style={{
                    background: mobileSelectable && selectedIds.includes(record.id) ? '#f0f7ff' : '#fff',
                    border: mobileSelectable && selectedIds.includes(record.id) ? '1px solid #2CBDFF' : '1px solid #d9d9d9',
                    borderRadius: 8,
                    padding: '10px 14px',
                    cursor: enableRowClick ? 'pointer' : undefined,
                    display: 'flex',
                    gap: mobileSelectable ? 10 : 0,
                    alignItems: 'flex-start',
                  }}
                >
                  {mobileSelectable && onSelectChange && (
                    <Checkbox
                      checked={selectedIds.includes(record.id)}
                      disabled={isSelectDisabled?.(record)}
                      onChange={(e) => {
                        e.stopPropagation();
                        onSelectChange(e.target.checked, record);
                      }}
                      onClick={(e) => e.stopPropagation()}
                      style={{ marginTop: 2 }}
                    />
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                  {visibleColumns.map((col: any, idx: number) => {
                    const value = col.dataIndex ? record[col.dataIndex] : record;
                    const rendered = col.render ? col.render(value, record, idx) : value;
                    if (rendered == null || rendered === '') return null;
                    return (
                      <div
                        key={col.key || col.dataIndex || idx}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '3px 0',
                          ...(idx === 0 ? { marginBottom: 4 } : {}),
                        }}
                      >
                        {col.title && (
                          <span style={{ fontSize: 11, color: '#8c8c8c', textTransform: 'uppercase', flexShrink: 0, marginRight: 8 }}>
                            {col.title}
                          </span>
                        )}
                        <span style={{ fontSize: 13, textAlign: 'right', flex: 1, minWidth: 0 }}>{rendered}</span>
                      </div>
                    );
                  })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Spin>

        {/* Load more */}
        {hasMore && (
          <Button
            block
            type="text"
            loading={loading}
            onClick={handleLoadMore}
            style={{ marginTop: 8, color: '#2CBDFF', fontWeight: 500 }}
          >
            Load more ({accumulatedData.length} of {totalCount})
          </Button>
        )}
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className='shared-table-container'
    >
      <div ref={tableRef}>
        <Table
          size={sizeType}
          locale={{ emptyText: loading ? 'Loading...' : emptyText }}
          rowKey={(record) => record.id.toString()}
          columns={columns}
          pagination={{
            showSizeChanger: true,
            pageSizeOptions: ['10', '15', '20', '30', '50', '100'],
            pageSize: maxResultCount,
            total: totalCount,
            defaultCurrent: 1,
            current: currentPage,
            size: 'middle',
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
          }}
          loading={{
            spinning: loading,
            tip: 'Loading...',
            indicator: <LoadingOutlined style={{ fontSize: 24 }} spin />,
          }}
          dataSource={dataSource}
          scroll={{ y: scrollY, x: true }}
          onChange={handlePageChange}
          summary={summary}
          expandable={expandable}
          onRow={
            enableRowClick
              ? (record) => ({
                  onClick: (event) => {
                    const target = event.target as HTMLElement;
                    if (target.closest('.ant-checkbox-wrapper') || target.closest('.ant-checkbox')) {
                      event.stopPropagation();
                      return;
                    }
                    handleRowClick(record);
                  },
                  style: { cursor: 'pointer' },
                })
              : undefined
          }
        >
          {children}
        </Table>
      </div>
    </div>
  );
};